import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; faker: any }
    | { kind: 'error'; message: string };

const LOCALES = [
    { value: 'en_US', label: 'USA (en_US)', country: 'United States' },
    { value: 'en_GB', label: 'UK (en_GB)', country: 'United Kingdom' },
    { value: 'en_CA', label: 'Canada (en_CA)', country: 'Canada' },
    { value: 'fr_CA', label: 'Canada – French (fr_CA)', country: 'Canada' },
    { value: 'es_MX', label: 'Mexico (es_MX)', country: 'Mexico' },
    { value: 'pt_BR', label: 'Brazil (pt_BR)', country: 'Brazil' },
    { value: 'de', label: 'Germany (de)', country: 'Germany' },
    { value: 'fr', label: 'France (fr)', country: 'France' },
    { value: 'it', label: 'Italy (it)', country: 'Italy' },
    { value: 'es', label: 'Spain (es)', country: 'Spain' },
    { value: 'ja', label: 'Japan (ja)', country: 'Japan' },
    { value: 'ko', label: 'Korea (ko)', country: 'South Korea' },
    { value: 'zh_CN', label: 'China (zh_CN)', country: 'China' },
    { value: 'hi_IN', label: 'India – Hindi', country: 'India' },
];

interface State {
    locale: string;
    count: number;
    format: 'oneline' | 'multiline' | 'json';
    output: string;
    lib: LibState;
    copied: boolean;
}

export default class FakeAddressGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        locale: 'en_US',
        count: 5,
        format: 'multiline',
        output: '',
        lib: { kind: 'idle' },
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async buildFaker(): Promise<any | null> {
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('@faker-js/faker');
            const all = mod.allLocales as Record<string, any>;
            const localeData = all[this.state.locale] || all.en;
            const faker = new mod.Faker({ locale: [localeData, all.en, all.base] });
            this.setState({ lib: { kind: 'ready', faker } });
            return faker;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load faker' } });
            return null;
        }
    }

    generate = async () => {
        const faker = await this.buildFaker();
        if (!faker) return;
        const localeMeta = LOCALES.find(l => l.value === this.state.locale);
        const country = localeMeta?.country || faker.location.country();
        const items: any[] = [];
        for (let i = 0; i < this.state.count; i++) {
            const addr = {
                street: faker.location.streetAddress(),
                city: faker.location.city(),
                state: faker.location.state ? faker.location.state() : '',
                postalCode: faker.location.zipCode(),
                country,
            };
            items.push(addr);
        }
        let text = '';
        if (this.state.format === 'json') {
            text = JSON.stringify(items, null, 2);
        } else if (this.state.format === 'oneline') {
            text = items.map(a => [a.street, a.city, a.state, a.postalCode, a.country].filter(Boolean).join(', ')).join('\n');
        } else {
            text = items.map(a => `${a.street}\n${a.city}${a.state ? ', ' + a.state : ''} ${a.postalCode}\n${a.country}`).join('\n\n');
        }
        this.setState({ output: text, copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { locale, count, format, output, lib, copied } = this.state;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.field}>
                    <label htmlFor="fa-locale">Locale</label>
                    <select id="fa-locale" value={locale}
                        onChange={e => this.setState({ locale: e.target.value, lib: { kind: 'idle' } }, this.generate)}>
                        {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                </div>
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="fa-format">Format</label>
                        <select id="fa-format" value={format}
                            onChange={e => this.setState({ format: e.target.value as any }, this.generate)}>
                            <option value="multiline">Multi-line</option>
                            <option value="oneline">One-line (comma-separated)</option>
                            <option value="json">JSON array</option>
                        </select>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="fa-count">Count</label>
                        <input id="fa-count" type="number" inputMode="decimal" min={1} max={500} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(500, +e.target.value || 5)) }, this.generate)} />
                    </div>
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                    disabled={lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                    {lib.kind === 'loading' ? 'Loading faker…' : '↻ Regenerate'}
                </button>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated addresses"
                previewMeta={output ? `${count} address${count === 1 ? '' : 'es'} · ${format}` : `Click Generate to produce ${count} sample address${count === 1 ? '' : 'es'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated addresses">{output || 'No addresses yet — click Generate.'}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
                error={lib.kind === 'error' ? (lib as any).message : null}
            />
        );
    }
}
